import {useFeatureValue} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import throttle from "lodash.throttle";
import {useCallback, useContext, useEffect, useRef} from "react";

import type {AppFeatures} from "@khan/analytics-growthbook/app-features";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {useRegionOverride} from "@khan/data-region-override";
import {
    getExperimentFeatures,
    getPromptVariables,
    maybeGetAsStudentPersona,
    track,
    untrack,
} from "@khan/guide-core-helpers";
import {getClassroomPageMetadata} from "@khan/guide-helpers-tutor-me";
import {GuideStateApi} from "@khan/guide-state";
import type {ImageInput, SendGuideItemOptions} from "@khan/guide-types";
import Log, {isErrorWithResponse, KAError} from "@khan/logging";
import {useUserAttributes} from "@khan/shared-core";
import {AppShellContext} from "@khan/shared-core/context";
import {useLocation} from "@khan/urls";
import {
    CancellationError,
    RetryPolicies,
    retryWithDelay,
} from "@khan/util-retry-with-delay";

import {useGuideAudioController} from "../hooks/use-guide-audio-controller.ts";
import {useGuidePreferences} from "../hooks/use-guide-preferences.ts";

import {appendConversationWithErrorMessage} from "./append-conversation-with-error-message.ts";
import {askAIGuide} from "./ask-ai-guide.ts";
import {
    DEFAULT_COOLDOWN_STATUS,
    ERROR_CODE_QUOTA_EXCEEDED,
    ERROR_CODE_RATE_LIMITED,
    STATUS_FORBIDDEN,
    STATUS_TOO_MANY_REQUESTS,
} from "./constants.ts";
import {
    ResumeConversationGenericError,
    ResumeCooldownError,
    ResumeUnownedThreadError,
} from "./errors.ts";
import {updateConversationWithError} from "./update-conversation-with-error.ts";
import {useMakeStreamingHandlers} from "./use-make-streaming-handlers.ts";
import {isChromebook} from "./utils/is-chromebook.ts";
import {
    khanmigoJsonErrorToKAError,
    khanmigoStreamErrorToJsonError,
} from "./utils/khanmigo-errors.ts";

/**
 * An item to send to the AI guide.
 */
type GuideItem = // An event.
    // A chat message.
    | {
          event: {
              name: string;
              params: Record<any, any>;
          };
      }
    | {
          message: string;
          actionBubble?: string | null | undefined;
          imageInputs?: Array<ImageInput>;
      }
    | {
          commandObject: {
              name: string;
              params: Record<any, any>;
          };
      }
    | {
          entryComponent: {
              name: string;
              input: Record<string, any>;
          };
      };

/**
 * Determines the type of a GuideItem for logging and tracking purposes.
 *
 * @param item The GuideItem to get the type of.
 * @returns A string describing the item type.
 */
function getItemType(item: GuideItem): string {
    if ("message" in item) {
        return "chat_message";
    }
    if ("event" in item) {
        return `event:${item.event.name}`;
    }
    if ("commandObject" in item) {
        return `command:${item.commandObject.name}`;
    }
    if ("entryComponent" in item) {
        return `entryComponent:${item.entryComponent.name}`;
    }
    return "unknown";
}

interface SendGuideItemFn {
    /**
     * Send an item to the AI guide service.
     *
     * @param command The command to send to the AI guide service.
     * @param item The item to send to the AI guide service.
     * @param options Options to customize the request and request handling.
     * @returns A promise that resolves when the request has completed.
     */
    (
        command: string,
        item: GuideItem,
        options?: SendGuideItemOptions,
    ): Promise<void>;
}

/**
 * Hook to obtain a function for sending an item to the AI guide service.
 *
 * @returns A function for sending an item to the AI guide service.
 */
export const useSendGuideItem = (): SendGuideItemFn => {
    const {pathname, href, queryParams} = useLocation();
    const userAttributes = useUserAttributes();
    const regionResult = useRegionOverride();
    const {entrypoint} = useContext(AppShellContext);
    const {selectedVoice, isVoiceOn} = useGuideAudioController();
    const {
        preferences: {guideLanguageCode},
    } = useGuidePreferences();
    const reactRenderSampleRate = useFeatureValue<
        AppFeatures["react-render-sample-rate"]
    >("react-render-sample-rate", 0);

    const assignmentIdFromQueryParams = queryParams["assignment"];

    const makeStreamingHandlers = useMakeStreamingHandlers(
        reactRenderSampleRate,
    );
    const cancelRetryRef = useRef(() => {}); // reference to a cancel method

    const getAnswerWait = () => {
        if (GuideStateApi.latest().bufferResponse) {
            return 0;
        }
        return isChromebook() ? 1000 : 200;
    };

    // Whenever the url changes, we cancel any active retry attempts.
    useEffect(() => {
        cancelRetryRef.current();
        GuideStateApi.set("retryTimeoutSeconds", 0);
    }, [pathname]);

    const _internalSendGuideItem = useCallback(
        async (
            command: string,
            item: GuideItem,
            {
                assignmentID,
                customPrompt,
                promptVersionOverride,
                demoStartDate,
                demoEndDate,
                generateVoice,
                onResponseStart,
                onUserPerceivedResponseStart,
                onResponseComplete,
                onResponseFailed,
            }: SendGuideItemOptions = {},
        ) => {
            try {
                await retryWithDelay(
                    async (retry, _, delayMs) => {
                        // Create and track the controller.
                        const controller = track();

                        const {
                            onAnswer,
                            onAudio,
                            onMetadata,
                            onStopId,
                            onCommand,
                            onActionStatus,
                        } = makeStreamingHandlers(
                            controller.signal,
                            onResponseComplete,
                        );
                        const throttledHandleAnswer = throttle(
                            onAnswer,
                            getAnswerWait(),
                        );
                        const shouldGenerateVoice =
                            isVoiceOn &&
                            (generateVoice || command === "chat_message");
                        // NOTE(reid): At the moment, coediting commands are
                        // handled in Go, and other commands are handled in
                        // Python. This bit of hackiness papers over that
                        // until we unify the two approaches.
                        const isCoeditingCommand =
                            command === "command" &&
                            "commandObject" in item &&
                            item.commandObject.name.startsWith("coediting/");
                        const isConversationTestCaseCommand =
                            command === "command" &&
                            "commandObject" in item &&
                            (item.commandObject.name ===
                                "associate_conversation_test_case" ||
                                item.commandObject.name ===
                                    "set_conversation_test_case_name" ||
                                item.commandObject.name ===
                                    "set_conversation_test_case_success");
                        // Requests that are type `command` are sent to python.
                        const shouldSendToPython =
                            command === "command" &&
                            !isCoeditingCommand &&
                            !isConversationTestCaseCommand;

                        GuideStateApi.set("menusDisabled", true);
                        GuideStateApi.set("retryTimeoutSeconds", 0);

                        const {pageType, pageTitle} = getClassroomPageMetadata(
                            entrypoint === "classroom",
                        );

                        // Check for cooldown right before making the API call
                        // to handle cases where cooldown is activated during retries
                        const currentCooldownStatus =
                            GuideStateApi.latest().cooldownStatus ??
                            DEFAULT_COOLDOWN_STATUS;
                        if (currentCooldownStatus.hasCooldown) {
                            const triggerContext = {
                                command,
                                itemType: getItemType(item),
                                hasActionBubble:
                                    "actionBubble" in item &&
                                    !!item.actionBubble,
                                hasMessage: "message" in item,
                                hasAttachedImages:
                                    "imageInputs" in item &&
                                    !!item.imageInputs?.length,
                                pathname,
                                entrypoint,
                            };

                            Log.log(
                                "[Cooldown] Guide item request blocked due to active cooldown.",
                                {
                                    cooldownType:
                                        currentCooldownStatus.cooldownType,
                                    triggerContext,
                                    message:
                                        "message" in item
                                            ? item.message?.substring(0, 50) +
                                              (item.message &&
                                              item.message.length > 50
                                                  ? "..."
                                                  : "")
                                            : undefined,
                                },
                            );

                            // Stop retrying by throwing an error
                            throw new Error(
                                "Request blocked due to active cooldown",
                            );
                        }

                        try {
                            await askAIGuide(
                                {
                                    pageTitle,
                                    pageType,
                                    persona:
                                        maybeGetAsStudentPersona(
                                            userAttributes,
                                            pathname,
                                            GuideStateApi.latest()
                                                .userAsStudent,
                                        ) ?? "",
                                    botName:
                                        GuideStateApi.latest()
                                            .selectedBotName ?? "",
                                    command,
                                    event:
                                        "event" in item
                                            ? item.event
                                            : undefined,
                                    essaySessionId:
                                        GuideStateApi.latest().essaySessionId ??
                                        undefined,
                                    commandObject:
                                        "commandObject" in item
                                            ? item.commandObject
                                            : undefined,
                                    entryComponent:
                                        "entryComponent" in item
                                            ? item.entryComponent
                                            : undefined,
                                    message:
                                        "message" in item
                                            ? item.message
                                            : undefined,
                                    imageInputs:
                                        "imageInputs" in item
                                            ? item.imageInputs
                                            : undefined,

                                    actionBubble:
                                        "actionBubble" in item
                                            ? item.actionBubble
                                            : undefined,
                                    promptStore: getPromptVariables(),
                                    contextualLinks:
                                        GuideStateApi.latest().contextualLinks,
                                    conversation:
                                        GuideStateApi.latest().conversation.filter(
                                            (entry) => !entry.uiOnly,
                                        ),
                                    customPrompt: customPrompt ?? "",
                                    url: href,
                                    region:
                                        regionResult.status === "success"
                                            ? regionResult.regionCode
                                            : "",
                                    thread_id: GuideStateApi.latest().threadId,
                                    contentUrlPath:
                                        GuideStateApi.latest().contentURLPath ??
                                        undefined,
                                    contentPathIds:
                                        GuideStateApi.latest().navigation
                                            .contentPathIds ?? undefined,
                                    dynamicBot:
                                        GuideStateApi.latest().dynamicBot,
                                    modelOverride:
                                        GuideStateApi.latest().modelOverride,
                                    temperatureOverride:
                                        GuideStateApi.latest()
                                            .temperatureOverride,
                                    // default to stream responses unless the user has
                                    // toggled on the reduce motion setting.
                                    stream: !GuideStateApi.latest()
                                        .bufferResponse,
                                    // Flags to support features in development.
                                    // For example, `inlineLinksAsMarkdown: "true"`
                                    // can be used to request response body links in
                                    // markdown format.
                                    flags: {
                                        inlineLinksAsMarkdown: "true",
                                        promptVersionOverride:
                                            promptVersionOverride ?? "",
                                        demoStartDate: demoStartDate ?? "",
                                        demoEndDate: demoEndDate ?? "",
                                    },
                                    sendToPython: shouldSendToPython,
                                    annotations:
                                        GuideStateApi.latest().annotations,
                                    generateVoice: shouldGenerateVoice,
                                    assignmentId:
                                        assignmentIdFromQueryParams ||
                                        assignmentID,
                                    selectedVoice: selectedVoice ?? undefined,
                                    selectedGuideLanguageCode:
                                        guideLanguageCode,
                                    isClassroom: entrypoint === "classroom",
                                    growthbookExperimentFeatures:
                                        getExperimentFeatures(),
                                },
                                controller.signal,
                                {
                                    onAnswer: throttledHandleAnswer,
                                    onAudio,
                                    onMetadata,
                                    onStopId,
                                    onCommand,
                                    onActionStatus,
                                },
                                onResponseStart,
                                onUserPerceivedResponseStart,
                            );
                        } catch (err: any) {
                            // We abort requests when we navigate away before
                            // the response has been completely resolved. These
                            // are expected so we just swallow the error.
                            if (controller.signal.aborted) {
                                return;
                            }
                            let jsonKaError: KAError | undefined;

                            if (err instanceof KAError) {
                                if (isErrorWithResponse(err)) {
                                    const response = err.response;
                                    const responseText = await response.text();
                                    jsonKaError = khanmigoJsonErrorToKAError(
                                        khanmigoStreamErrorToJsonError(
                                            responseText,
                                        ),
                                    );
                                }
                                onResponseFailed?.(jsonKaError ?? err);
                            }

                            if (
                                err?.response?.status ===
                                STATUS_TOO_MANY_REQUESTS
                            ) {
                                const res = await err.response.json();
                                // This indicates that the user has exceeded
                                // their daily token limit.
                                if (res.error === ERROR_CODE_QUOTA_EXCEEDED) {
                                    GuideStateApi.set("remainingQuota", 0);
                                    GuideStateApi.set(
                                        "conversation",
                                        (conversation) =>
                                            appendConversationWithErrorMessage(
                                                conversation,
                                                t`I'm sorry, I am out of AI power for today. Please come back to me tomorrow.`,
                                            ),
                                    );
                                    publishAnalyticsEventForMinorConversion({
                                        eventType:
                                            "AiGuideBotErrorMessageReceived",
                                        eventSchemaVersion: 2,
                                        aiGuideThreadId:
                                            GuideStateApi.latest().threadId ??
                                            "",
                                        aiGuideNavigationId:
                                            GuideStateApi.latest().navigation
                                                .id,
                                        aiGuideInteractionId: "",
                                        errorKind: "REACHED_USAGE_QUOTA",
                                    });
                                    return;
                                }

                                if (res.error === ERROR_CODE_RATE_LIMITED) {
                                    GuideStateApi.set(
                                        "retryTimeoutSeconds",
                                        delayMs / 1000,
                                    );
                                    const {result, cancel} = retry();
                                    cancelRetryRef.current = cancel;
                                    await result;
                                    return;
                                }
                            }
                            if (
                                "event" in item &&
                                item.event.name === "resume_conversation"
                            ) {
                                // If the guide is on cooldown, throw a
                                // ResumeCooldownError to be propagated
                                if (
                                    jsonKaError?.message?.includes(
                                        "Cooldown in effect",
                                    )
                                ) {
                                    throw ResumeCooldownError;
                                }

                                // If the user attempts to resume a thread they do not
                                // own, throw a ResumeUnownedThreadError to be propagated
                                if (
                                    err?.response?.status === STATUS_FORBIDDEN
                                ) {
                                    throw ResumeUnownedThreadError;
                                }

                                // Otherwise, throw a ResumeConversationGenericError to be propagated
                                throw ResumeConversationGenericError;
                            }

                            // If we encounter an error but the user hasn't
                            // reached their quota, then we'll indicate that
                            // the last outgoing message encountered an error
                            // and allow users to resend it.
                            GuideStateApi.set("conversation", (conversation) =>
                                updateConversationWithError(conversation),
                            );

                            publishAnalyticsEventForMinorConversion({
                                eventType: "AiGuideBotErrorMessageReceived",
                                eventSchemaVersion: 2,
                                aiGuideThreadId:
                                    GuideStateApi.latest().threadId ?? "",
                                aiGuideNavigationId:
                                    GuideStateApi.latest().navigation.id,
                                aiGuideInteractionId: "",
                                errorKind: "GENERAL",
                            });
                        } finally {
                            // Cancel any pending answer handling; we're about
                            // to show the final answer anyway.
                            throttledHandleAnswer.cancel();

                            // Remove the abort controller for this request
                            // from our tracked controllers.
                            untrack(controller);

                            GuideStateApi.set("stopId", null);
                            GuideStateApi.set("menusDisabled", false);
                            GuideStateApi.set("annotations", []);
                        }
                    },
                    {
                        policy: RetryPolicies.Fixed,
                        initialDelayMs: 5000,
                        maxRetries: 3,
                    },
                );
            } catch (err) {
                // Cancellation errors are expected when navigation occurs
                // between retries. In that scenario, we don't need to do any
                // extra handling of the error.
                if (err === CancellationError) {
                    return;
                }

                // Propagate error from resuming conversation to be handled by
                // the useResumeConversation hook
                if (
                    err === ResumeCooldownError ||
                    err === ResumeUnownedThreadError ||
                    err === ResumeConversationGenericError
                ) {
                    throw err;
                }

                // If we've reached the max retry attempts we'll update the
                // conversation and allow the user to manually resend their
                // request
                GuideStateApi.set("conversation", (conversation) =>
                    updateConversationWithError(conversation),
                );

                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideBotErrorMessageReceived",
                    eventSchemaVersion: 2,
                    aiGuideThreadId: GuideStateApi.latest().threadId ?? "",
                    aiGuideNavigationId: GuideStateApi.latest().navigation.id,
                    aiGuideInteractionId: "",
                    errorKind: "GENERAL",
                });
            }
        },
        [
            href,
            isVoiceOn,
            makeStreamingHandlers,
            pathname,
            userAttributes,
            regionResult,
            selectedVoice,
            assignmentIdFromQueryParams,
            guideLanguageCode,
            entrypoint,
        ],
    );

    // Function to perform the actual request.
    const sendGuideItem: SendGuideItemFn = useCallback(
        async (
            command: string,
            item: GuideItem,
            {
                assignmentID,
                customPrompt,
                promptVersionOverride,
                demoStartDate,
                demoEndDate,
                generateVoice,
                onResponseStart,
                onUserPerceivedResponseStart,
                onResponseComplete,
                onResponseFailed,
            }: SendGuideItemOptions = {},
        ) => {
            await _internalSendGuideItem(command, item, {
                assignmentID,
                customPrompt,
                promptVersionOverride,
                demoStartDate,
                demoEndDate,
                generateVoice,
                onResponseStart,
                onUserPerceivedResponseStart,
                onResponseComplete,
                onResponseFailed,
            });
            return;
        },
        [_internalSendGuideItem],
    );

    return sendGuideItem;
};
